import { AutoAwesomeOutlined, DocumentScannerOutlined } from "@mui/icons-material";
import { Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import { UUID } from "crypto";
import { useLocation, useParams } from "react-router-dom";
import { BodyPanel, MainPanel, RightPanel } from "../Layout/Panels";
import StudyHeader from "../Layout/StudyHeader";
import useStudyMetadataStore from "../useStudyMetadataStore";
import useStudyTranscriptStore from "../useStudyTranscriptStore";
import InterviewTranscript from "./InterviewTranscript";
import ChangeStudyButton from "../ChangeStudyButton";

interface RawTranscriptItem {
  speaker: string;
  text: string;
}

interface UriParams {
  studyId: UUID;
  respondentId: UUID;
}

export default function ViewTranscript() {
  const { studyId, respondentId } = useParams() as unknown as UriParams;
  const { study } = useStudyMetadataStore(studyId as UUID);

  const TranscriptStore = useStudyTranscriptStore({ studyId, respondentId });
  const location = useLocation();
  const { fromRightPanel } = location.state || { fromRightPanel: false };

  let jsonFormattingError = false;
  let transcriptData: RawTranscriptItem[] = [];

  const isLoadingTranscript = TranscriptStore.isLoading;
  const transcriptJson = TranscriptStore?.data?.transcript;

  if (transcriptJson) {
    try {
      transcriptData = JSON.parse(transcriptJson) as RawTranscriptItem[];
    } catch (error) {
      console.error(error);
      jsonFormattingError = true;
    }
  }

  let lastSpeaker = "";

  return (
    <MainPanel>
      {isLoadingTranscript ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", width: "100%" }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <BodyPanel>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  flexDirection={"row"}
                >
                  <StudyHeader study={study} />
                  <ChangeStudyButton />
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", mt: "2rem", gap: "0.25rem" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box>
                    <AutoAwesomeOutlined sx={{ fill: "#13294B", verticalAlign: "sub", marginRight: "0.5em" }} />
                    <Typography variant="h6" color={"#13294B"} display="inline-block">
                      Deepsense Summary
                    </Typography>
                  </Box>
                  {/* <Feedback /> */}
                </Box>
                <Card sx={{ marginTop: "1rem", marginBottom: "1rem" }}>
                  <Box sx={{ padding: "1rem" }}>
                    {formatResponseText(TranscriptStore.data?.transcript_summary ?? "")}
                  </Box>
                </Card>
                <Box>
                  <DocumentScannerOutlined sx={{ fill: "#13294B", verticalAlign: "sub", marginRight: "0.5em" }} />
                  <Typography variant="h6" color={"#13294B"} display="inline-block">
                    Transcript
                  </Typography>
                </Box>
                <Card sx={{ marginTop: "1rem", marginBottom: "1rem" }}>
                  <Box
                    sx={{
                      padding: "1rem",
                      "& .item": {},
                      "& .item-speaker": { fontWeight: "bold", paddingRight: "0.5em", float: "left" },
                      "& .item-text": { marginLeft: "110px" },
                    }}
                  >
                    {!jsonFormattingError ? (
                      transcriptData.map((item, i) => {
                        // Little bit of logic to attempt to visually separate back and forth's between participants
                        const isDifferentSpeaker =
                          lastSpeaker && lastSpeaker != item.speaker && lastSpeaker != "Interviewer";
                        lastSpeaker = item.speaker;

                        return (
                          <Box key={i} className="item" sx={isDifferentSpeaker ? { mt: "1em" } : {}}>
                            <Typography className="item-speaker">{item.speaker}: </Typography>
                            <Typography className="item-text">{item.text}</Typography>
                          </Box>
                        );
                      })
                    ) : (
                      <Box>
                        <Typography sx={{ fontWeight: "bold", mb: "1em" }}>
                          Malformed JSON, displaying raw transcript:
                        </Typography>
                        <Typography>{transcriptJson}</Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Box>
            </Box>
          </BodyPanel>

          <RightPanel>
            <InterviewTranscript studyId={studyId} respondentId={respondentId} fromRightPanel={fromRightPanel} />
          </RightPanel>
        </>
      )}
    </MainPanel>
  );
}

export const formatResponseText = (text: string, boldFontSize?: string, otherFontSize?: string) => {
  const response = (text || "").split("\n").flatMap((segment, i) => {
    const isListItem = segment.startsWith("- ");
    const isBlankLine = !segment;

    if (isListItem) {
      // Clean off the "- " and make element a <li>
      return <li key={i}>{segment.replace(/^(- )/, "")}</li>;
    }

    if (segment.endsWith(":")) {
      return (
        <Typography sx={{ fontWeight: "bold", fontSize: boldFontSize ? boldFontSize : "auto" }} key={i}>
          {segment}
        </Typography>
      );
    }

    return (
      <Typography py={isBlankLine ? "0.5em" : 0} key={i} sx={{ fontSize: otherFontSize ? otherFontSize : "auto" }}>
        {segment}
      </Typography>
    );
  });

  return response;
};
